// ── PolicyPage ──────────────────────────────────────────────────────────────────
// One renderer for the merchant policies in config/policies.js. A payment gateway reviewer opens
// these URLs by hand during onboarding, so each policy has its own route and each page has to stand
// on its own: a visible title, the company behind it, a contact route, and no login wall.
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { IS_FEATURE_ENABLED_QUERY } from '../graphql';
import { POLICIES, POLICY_LINKS, POLICY_FLAG, COMPANY, REGISTERED_OFFICE, CONTACT_EMAIL, SUPPORT_HOURS, LAST_UPDATED } from '../config/policies';

const P = ({ children }) => (
  <p style={{ fontSize: 'var(--font-sm)', color: 'var(--text-muted)', lineHeight: 1.8, marginBottom: 14 }}>{children}</p>
);

export default function PolicyPage({ slug }) {
  const location = useLocation();
  const policy = POLICIES[slug];
  // Gated, but deliberately FAIL OPEN: only an explicit `false` hides the page. While the query is
  // in flight, or if it errors, or if the API is down, the policy still renders — a compliance page
  // that disappears during an API blip fails a gateway review for a reason unrelated to its content.
  const { data } = useQuery(IS_FEATURE_ENABLED_QUERY, {
    variables: { key: POLICY_FLAG }, fetchPolicy: 'cache-and-network', errorPolicy: 'ignore',
  });
  const hidden = data?.isFeatureEnabled === false;

  if (!policy) return null;
  if (hidden) {
    return (
      <div className="page">
        <div className="container" style={{ padding: '80px 16px', maxWidth: 560, textAlign: 'center' }}>
          <div style={{ fontSize: 44, marginBottom: 10 }}>📄</div>
          <h1 style={{ fontSize: 'var(--font-lg)', fontWeight: 800, marginBottom: 8 }}>{policy.title}</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-sm)', lineHeight: 1.7, marginBottom: 16 }}>
            This policy is being updated and isn't available right now. For anything urgent, email{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: 'var(--primary)' }}>{CONTACT_EMAIL}</a>.
          </p>
          <Link to="/" className="btn btn-primary btn-sm">Back to Ledxo Hub</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="container" style={{ padding: '32px 16px', maxWidth: 800 }}>
        <h1 style={{ fontSize: 'calc(var(--font-xl) + 4px)', fontWeight: 900, marginBottom: 8 }}>{policy.title}</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-sm)', marginBottom: 28 }}>
          {COMPANY} · Last updated: {LAST_UPDATED}
        </p>

        {policy.intro.map((t, i) => <P key={i}>{t}</P>)}

        {policy.listIntro && <P>{policy.listIntro}</P>}

        {policy.items.length > 0 && (
          <ol style={{ paddingLeft: 20, marginBottom: 24 }}>
            {policy.items.map((t, i) => (
              <li key={i} style={{ fontSize: 'var(--font-sm)', color: 'var(--text-muted)', lineHeight: 1.8, marginBottom: 12 }}>{t}</li>
            ))}
          </ol>
        )}

        {/* Onboarding reviewers look for a reachable human, not just prose. */}
        <div className="card" style={{ background: 'var(--surface2)', marginTop: 32 }}>
          <div style={{ fontWeight: 800, marginBottom: 8 }}>Contact us</div>
          <P>
            {COMPANY}, {REGISTERED_OFFICE}
            <br />
            Email: <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: 'var(--primary)' }}>{CONTACT_EMAIL}</a>
            <br />
            Hours: {SUPPORT_HOURS}
          </P>
        </div>

        <div style={{ marginTop: 28, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          {POLICY_LINKS.filter(l => l.to !== location.pathname).map(l => (
            <Link key={l.to} to={l.to} style={{ fontSize: 'var(--font-sm)', color: 'var(--primary)', textDecoration: 'none', fontWeight: 600 }}>
              {l.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
